import { useContext, useEffect, useState } from 'react'
import { Button, DialogActions, DialogContent, Typography, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import { AppContext } from '../../providers/app-provider'
import Api from '../../utils/api'
import useSharedStyles from '../../utils/shared.styles'
import DialogWithTitle from '../dialog-with-title'

import FolderSelectionSearch from './folder-selection-search'
import useStyles from './index.styles'

interface IProps {
  open: boolean
  onClose: () => void
  currentFolderId: number
  currentFolderName: string
  currentParentFolderName: string
}

const MoveFolderModal = (props: IProps) => {
  const { open, onClose, currentFolderId, currentFolderName, currentParentFolderName } = props

  const classes = useStyles
  const sharedClasses = useSharedStyles
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const { host, refreshPage } = useContext(AppContext)

  const [selectedFolder, setSelectedFolder] = useState<ISearchResultAccount>()
  const [showSearch, setShowSearch] = useState<boolean>(open)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (open) setShowSearch(true)
  }, [open])

  const closeModal = () => {
    // Reset state and call onClose
    setSelectedFolder(undefined)
    setIsSubmitting(false)
    setOpenModal(false)
    onClose()
  }

  const handleSubmit = async () => {
    if (!selectedFolder) return
    setIsSubmitting(true)
    const res = await Api.put(`${host}/api/folders/${currentFolderId}`, { parentFolderId: selectedFolder.id })
    if (!res.ok) {
      enqueueSnackbar('Unable to move folder. Please try again.', { variant: 'error' })
    } else {
      enqueueSnackbar('Folder moved successfully!', { variant: 'success' })
      closeModal()
      refreshPage()
    }
    setIsSubmitting(false)
  }

  return (
    <>
      <DialogWithTitle setOpen={setOpenModal} open={openModal} title="Review and Confirm Move" maxWidth="sm" fullWidth={true}>
        <DialogContent>
          <Typography variant="subtitle2">You are moving the following folder:</Typography>
          <Typography variant="body1">{currentFolderName}</Typography>

          <div style={classes.moveFolderCurrentLocationContainer(theme)}>
            <Typography variant="subtitle2" sx={sharedClasses.italicText}>
              Current Location:
            </Typography>
            <Typography variant="body1">{currentParentFolderName}</Typography>
          </div>

          <div style={classes.moveFolderNewLocationContainer(theme)}>
            <Typography variant="subtitle2" color="primary" sx={sharedClasses.italicText}>
              New Location:
            </Typography>
            <Typography variant="body1">{selectedFolder?.name}</Typography>
          </div>
        </DialogContent>

        <DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closeModal}>
              Cancel
            </Button>

            <Button variant="contained" onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
              Move Folder
            </Button>
          </DialogActions>
        </DialogContent>
      </DialogWithTitle>
      <FolderSelectionSearch
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        onClose={onClose}
        setOpenModal={setOpenModal}
        setSelectedFolder={setSelectedFolder}
      />
    </>
  )
}

export default MoveFolderModal
